
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AddDocument.css';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useDocuments } from './context/DocumentsContext.js';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const ALLOWED_EXTENSIONS = ['docx', 'doc', 'pdf', 'jpeg', 'jpg', 'png', 'txt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const AddDocument = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { setDocuments } = useDocuments();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dept_id: '',
    doc_date: null,
    description: '',
    file: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const fileInputRef = useRef(null);

  const getUploadErrorMessage = (err) => {
    const status = err.response?.status;
    if (status === 413) {
      return t('documents.add.errors.fileTooLargeServer');
    }

    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (Array.isArray(data?.errors)) return data.errors.join(', ');
    return err.message || t('documents.add.errors.uploadFailed');
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!user) return;
      const orgId = user.department?.costCenter?.organization?.orgId;
      if (!orgId) return;
      try {
        setDeptLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}admin/department?orgId=${orgId}`
        );
        console.log('Department API response:', response.data);
        const list = (response.data || []).map(d => ({
          id: d.deptId ?? d.id,
          name: d.deptName ?? d.name,
        }));
        setDepartments(list);
      } catch (err) {
        console.error('Failed to load departments:', err);
      } finally {
        setDeptLoading(false);
      }
    };
    fetchDepartments();
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.dept_id) newErrors.dept_id = t('documents.add.validation.departmentRequired');
    if (!formData.doc_date) newErrors.doc_date = t('documents.add.validation.dateRequired');
    if (formData.description.length > 512) {
      newErrors.description = t('documents.add.validation.descriptionMaxLength');
    }
    if (!formData.file) newErrors.file = t('documents.add.validation.fileRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const picked = files[0];
      if (!picked) {
        setFormData(prev => ({ ...prev, file: null }));
        return;
      }
      const ext = picked.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setErrors(prev => ({
          ...prev,
          file: t('documents.add.validation.unsupportedType', {
            types: ALLOWED_EXTENSIONS.join(', '),
          }),
        }));
        setFormData(prev => ({ ...prev, file: null }));
        e.target.value = '';
        return;
      }
      if (picked.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          file: t('documents.add.validation.fileTooLarge'),
        }));
        setFormData(prev => ({ ...prev, file: null }));
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, file: picked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, doc_date: date }));
    if (errors.doc_date) {
      setErrors(prev => ({ ...prev, doc_date: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setErrors({});

      if (!validateForm()) {
        setIsSubmitting(false);
        return;
      }

      const formPayload = new FormData();

      formPayload.append('file', formData.file);
      formPayload.append('departmentId', formData.dept_id);
      formPayload.append('date', formData.doc_date.toISOString().split("T")[0]);
      formPayload.append('description', formData.description || '');
      formPayload.append('assignedUids', user.userId);
      formPayload.append('assignedTypeids', '');

      for (let pair of formPayload.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await axios.post(
        `${API_BASE_URL}document/upload`,
        formPayload,
        { 
          "Content-Type": "multipart/form-data"
        }
      );

      const doc = response.data;
      const newRow = {
        doc_id: doc.docId || "",
        dept_name: doc.department?.deptName || "",
        file_name: doc.fileName || "",
        file_type: doc.fileType || "",
        file_size: doc.fileSize || -1,
        doc_date: doc.docDate || "",
        create_uid: doc.createUid || "",
        user_name: `${doc.owner?.lastName ?? ""} ${doc.owner?.firstName ?? ""}`.trim(),
        create_ts: doc.createTs || "",
        file_url: doc.fileUrl || "",
      };
      setDocuments(prev => [newRow, ...prev]);

      setIsSubmitted(true);
      navigate('/documents');
    } catch (err) {
      console.error('Submit error:', err);
      console.error('Error response:', err.response?.data);
      setErrors({ submit: getUploadErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-document-container">
      <div className="add-document-card">
        <div className="add-document-title">{t('documents.add.title')}</div>
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="dept_id">{t('documents.add.department')}</label>
              <select
                name="dept_id"
                value={formData.dept_id}
                onChange={handleChange}
                className={errors.dept_id ? 'error' : ''}
              >
                <option value="">
                  {deptLoading ? t('documents.add.loading') : t('documents.add.selectDepartment')}
                </option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
              {errors.dept_id && <div className="error-message">{errors.dept_id}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="doc_date">{t('documents.add.date')}</label>
              <DatePicker
                selected={formData.doc_date}
                onChange={handleDateChange}
                className={`date-input${errors.doc_date ? ' error' : ''}`}
                wrapperClassName="add-document-datepicker-wrapper"
                dateFormat="yyyy-MM-dd"
                placeholderText={t('documents.add.selectDate')}
              />
              {errors.doc_date && <div className="error-message">{errors.doc_date}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="description">{t('documents.add.description')}</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={512}
                rows={4}
                className={errors.description ? 'error' : ''}
                placeholder={t('documents.add.descriptionPlaceholder')}
              />
              <div style={{ fontSize: '0.9rem', color: '#888', marginTop: '2px' }}>{formData.description.length}/512</div>
              {errors.description && <div className="error-message">{errors.description}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="file">{t('documents.add.uploadFile')}</label>
              <div className={`file-input-wrapper${errors.file ? ' error' : ''}`}>
                <input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  name="file"
                  onChange={handleChange}
                  className="file-input-hidden"
                  accept=".docx,.doc,.pdf,.jpeg,.jpg,.png,.txt"
                />
                <button
                  type="button"
                  className="file-input-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('documents.add.chooseFile')}
                </button>
                <span className="file-input-name">
                  {formData.file?.name || t('documents.add.noFileChosen')}
                </span>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#888', marginTop: '2px' }}>{t('documents.add.fileHint')}</div>
              {errors.file && <div className="error-message">{errors.file}</div>}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {t('documents.add.submit')}
            </button>
            <button type="button" className="add-document-back-btn" onClick={() => navigate('/documents')}>
              {t('documents.add.back')}
            </button>
          </div>
          {errors.submit && (
            <div className="add-document-error-text" role="alert">
              {errors.submit}
            </div>
          )}
          {isSubmitted && <div className="add-document-success-text">{t('documents.add.success')}</div>}
        </form>
      </div>
    </div>
  );
};

export default AddDocument;
